import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { filter, map, startWith } from 'rxjs/operators';
import { toSignal } from '@angular/core/rxjs-interop';
import { IconComponent } from '../../../shared/ui';
import { ReportIssueModalComponent } from '../report-issue-modal/report-issue-modal.component';
import { moduleFromRoute } from '../../data-access/route-module.map';

/**
 * The persistent "Report an issue" affordance, mounted once in ShellLayout so it is
 * present on every authenticated page.
 *
 * It opens the form as an overlay on the CURRENT page, pre-filled with the module
 * inferred from the current route and the exact current URL. That is the whole point:
 * the sidebar entry navigates the user away from the screen that is broken, which
 * destroys the context we are trying to collect.
 */
@Component({
  selector: 'app-report-issue-widget',
  standalone: true,
  imports: [CommonModule, IconComponent, ReportIssueModalComponent],
  template: `
    <!-- Hidden on the Support routes themselves: a floating "Report an issue" button on
         top of the report form is pure furniture, and on My Issues / Issue Detail the page
         already offers the action as a real button. -->
    @if (!isOpen() && !onSupportRoute()) {
      <button
        type="button"
        class="report-issue-fab"
        (click)="open()"
        aria-label="Report an issue"
        title="Report an issue">
        <app-icon name="bug" size="sm" />
      </button>
    }

    <app-report-issue-modal
      [isOpen]="isOpen()"
      [initialModule]="currentModule()"
      (closed)="close()" />
  `,
  styleUrl: './report-issue-widget.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ReportIssueWidgetComponent {
  private readonly router = inject(Router);

  readonly isOpen = signal(false);

  /**
   * Recomputed on every navigation so the module reflects wherever the user is when
   * they click — not wherever they happened to be when the app booted.
   */
  readonly currentModule = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map((e) => moduleFromRoute(e.urlAfterRedirects)),
      startWith(moduleFromRoute(this.router.url))
    ),
    { initialValue: moduleFromRoute(this.router.url) }
  );

  private readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map((e) => e.urlAfterRedirects),
      startWith(this.router.url)
    ),
    { initialValue: this.router.url }
  );

  readonly onSupportRoute = computed(() => this.currentUrl().startsWith('/app/support'));

  open(): void {
    this.isOpen.set(true);
  }

  close(): void {
    this.isOpen.set(false);
  }
}
